import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { DEFAULT_CONTENT } from "@/config/content";
import { api } from "@/lib/api";

const ContentContext = createContext({
  content: DEFAULT_CONTENT,
  loading: true,
  refresh: async () => {},
});

// Deep-merge remote overrides on top of defaults. Arrays are replaced wholesale
// so admin edits to chapters/marquee/timeSlots override the defaults exactly.
function merge(defaults, override) {
  if (!override || typeof override !== "object") return defaults;
  if (Array.isArray(defaults) || Array.isArray(override)) return override ?? defaults;
  const out = { ...defaults };
  for (const k of Object.keys(override)) {
    if (
      override[k] &&
      typeof override[k] === "object" &&
      !Array.isArray(override[k]) &&
      defaults[k] &&
      typeof defaults[k] === "object" &&
      !Array.isArray(defaults[k])
    ) {
      out[k] = merge(defaults[k], override[k]);
    } else if (override[k] !== undefined && override[k] !== null) {
      out[k] = override[k];
    }
  }
  return out;
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setContent(data ? merge(DEFAULT_CONTENT, data) : DEFAULT_CONTENT);
    } catch {
      setContent(DEFAULT_CONTENT);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <ContentContext.Provider value={{ content, loading, refresh }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}
